// How a listing reaches the buyer: local pickup, shipping, or both. One helper for the listing
// page (badge near the price, ships-within / est-arrival lines, the "Next:" hint) so they can't
// disagree. Prod (listing 571afaa2, pickup only): delivery_method / pickup_available were null and
// the page showed "Ships within 3 business days" + an arrival estimate under a meetup-only cart.
//
// Sources, in order of trust, any of which may be null on older rows:
//   delivery_method 'pickup' | 'ship' | 'both' and pickup_available (saved since this fix),
//   shipping_available / local_pickup_only (what the wizard has always saved; local_pickup_only
//   means "meetup allowed", see allow_meetup), and as a last resort a pickup city when the listing
//   does not ship.

const clean = (v) => {
  const s = v == null ? '' : String(v).trim();
  return s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined' ? s : '';
};

export function fulfillmentPlace(l) {
  return [clean(l?.location_city), clean(l?.location_state)].filter(Boolean).join(', ');
}

/**
 * @param {object} l listing row from GET /api/listings/:id
 * @param {(n:number)=>string} [formatPrice] used for a flat shipping price, if the listing has one
 */
export function getFulfillment(l, formatPrice) {
  const none = { pickup: false, ship: false, pickupOnly: false, shipOnly: false, both: false,
    digital: false, freeShipping: false, place: '', lines: [], label: '' };
  if (!l || typeof l !== 'object') return none;
  if (l.listing_type === 'digital') return { ...none, digital: true };

  const dm = clean(l.delivery_method).toLowerCase();
  const place = fulfillmentPlace(l);
  const ship = l.shipping_available === true || dm === 'ship' || dm === 'shipping' || dm === 'both';
  const pickup = l.pickup_available === true || dm === 'pickup' || dm === 'both'
    || l.local_pickup_only === true
    || (l.shipping_available === false && !!place);

  const freeShipping = ship && l.free_shipping === true;
  const flat = parseFloat(l.shipping_price ?? l.flat_shipping_price);
  const fmt = typeof formatPrice === 'function' ? formatPrice : (n) => `$${Number(n).toFixed(2)}`;
  const shipText = freeShipping ? 'Free shipping'
    : (Number.isFinite(flat) && flat > 0 ? `Ships · ${fmt(flat)}` : 'Ships · rate at checkout');

  const lines = [];
  if (pickup) lines.push(`${ship ? 'Local pickup' : 'Local pickup only'}${place ? ` · ${place}` : ''}`);
  if (ship) lines.push(shipText);

  return {
    pickup, ship, pickupOnly: pickup && !ship, shipOnly: ship && !pickup, both: pickup && ship,
    digital: false, freeShipping, place, lines, label: lines.join(' or '),
  };
}

// UX r4 F1: when escrowed funds reach the seller, worded for how the order is fulfilled, so a
// pickup order never reads "released 3 days after delivery". Pickup defaults mirror
// backend/utils/pickupAutoRelease.js (PICKUP_AUTO_RELEASE_DAYS = 5 after the end of the meetup
// date, PICKUP_AUTO_RELEASE_NO_DATE_DAYS = 7 after payment when no date was set); set the
// REACT_APP_ versions at build time only if the backend env is changed. Shipped orders: 3 days
// after delivery (jobs/escrowAutoRelease.js).
const envDays = (v, dflt) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 && n <= 60 ? n : dflt;
};
export const PICKUP_RELEASE_DAYS = {
  afterMeetup: envDays(process.env.REACT_APP_PICKUP_AUTO_RELEASE_DAYS, 5),
  noMeetupDate: envDays(process.env.REACT_APP_PICKUP_AUTO_RELEASE_NO_DATE_DAYS, 7),
};
export const SHIPPED_RELEASE_DAYS = 3;

/**
 * @param {'pickup'|'ship'|'rent'} kind
 * @param {{ hasMeetupDate?: boolean }} [opts] true/false when known; omitted = say both rules
 */
export function escrowReleaseCopy(kind, opts = {}) {
  const { afterMeetup: a, noMeetupDate: b } = PICKUP_RELEASE_DAYS;
  if (kind === 'pickup') {
    const auto = opts.hasMeetupDate === true ? `${a} days after the meetup date`
      : opts.hasMeetupDate === false ? `${b} days after payment (no meetup date was set)`
      : `${a} days after the meetup date (${b} days after payment if no date is set)`;
    return `Funds are released to the seller after you confirm pickup, or automatically ${auto}, unless you open a dispute.`;
  }
  if (kind === 'rent') return 'Your rental payment is held in escrow until pickup is confirmed.';
  return `Funds are released to the seller ${SHIPPED_RELEASE_DAYS} days after delivery unless you open a dispute.`;
}

// UX r5 (b): Messages quick replies. "Can you ship this?" is not offered when the thread's
// listing is pickup-only or the order in the thread is a pickup (meetup) order. Conversations from
// GET /api/messages carry the listing's fulfillment fields with a listing_ prefix; when they are
// missing (older backend) nothing is known, so every reply is shown, as before.
export const SHIP_QUICK_REPLY = 'Can you ship this?';
export const MESSAGE_QUICK_REPLIES = ['Is this still available?', "What's your best price?", SHIP_QUICK_REPLY,
  'Any defects I should know about?', 'When can we meet?'];

export function conversationListing(c) {
  if (!c || typeof c !== 'object' || !c.listing_id) return null;
  return {
    listing_type: c.listing_kind, delivery_method: c.listing_delivery_method,
    pickup_available: c.listing_pickup_available, shipping_available: c.listing_shipping_available,
    local_pickup_only: c.listing_local_pickup_only,
    location_city: c.listing_location_city, location_state: c.listing_location_state,
  };
}

export function isPickupOnlyConversation(conv, order) {
  if (order && (clean(order.meetup_location_name) || clean(order.meetup_location_address))) return true;
  const l = conversationListing(conv);
  return !!l && getFulfillment(l).pickupOnly;
}

export function messageQuickReplies(pickupOnly) {
  return pickupOnly ? MESSAGE_QUICK_REPLIES.filter(q => q !== SHIP_QUICK_REPLY) : MESSAGE_QUICK_REPLIES.slice();
}

export default getFulfillment;
